import styles from './PDFCompareViewer.module.scss'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { Document, Page, pdfjs } from 'react-pdf'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Tooltip, Button, Card, Skeleton, Progress, Radio, Tag } from 'antd'

import { FileSearchOutlined } from '@ant-design/icons'
import { ZoomFloatingActions } from './ZoomFloatingActions'
import classNames from 'classnames'
import { usePixelDiff, DETECTION_SCALE } from './pdf-comparison/use-pixel-diff'
import { alignPages } from './pdf-comparison/align-pages'

pdfjs.GlobalWorkerOptions.workerSrc = './pdf.worker.js'

const MARGIN_BETWEEN_PAGES = 18

// Compare text elements on aligned pages
const compareTextElements = (elements1, elements2, pageIndex) => {
  const diffs = []
  const tolerance = 15 // Adjust this value as needed for position tolerance
  let diffIndex = 0

  const areElementsEqual = (el1, el2) => {
    const [x1, y1] = el1.transform || [0, 0]
    const [x2, y2] = el2.transform || [0, 0]

    const positionEqual = Math.abs(x1 - x2) <= tolerance && Math.abs(y1 - y2) <= tolerance
    return positionEqual && el1.str === el2.str
  }

  elements1.forEach((el1) => {
    const matchingElement = elements2.find(el2 => areElementsEqual(el1, el2))
    if (!matchingElement) {
      diffs.push({ id: `diff-${pageIndex}-${diffIndex++}`, type: 'removed', oldElement: el1 })
    }
  })

  elements2.forEach((el2) => {
    const matchingElement = elements1.find(el1 => areElementsEqual(el1, el2))
    if (!matchingElement) {
      diffs.push({ id: `diff-${pageIndex}-${diffIndex++}`, type: 'added', newElement: el2 })
    }
  })

  return diffs
}

const extractElements = async (pdf) => {
  const elements = []
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const textContent = await page.getTextContent()
    const pageElements = textContent.items.map(item => ({
      str: item.str,
      transform: item.transform ? item.transform.slice(4, 6) : [0, 0],
      fontName: item.fontName,
      width: item.width,
      height: item.height,
    }))
    elements.push(pageElements)
  }
  return elements
}

// Extract elements from PDFs
const onDocumentLoad = async (pdf, setElements) => {
  const elements = await extractElements(pdf)
  setElements(elements)
}

const ProgressOverlay = ({ progress, description = 'Please wait while we analyze the documents...' }) => (
  <div className={styles.progressOverlay}>
    <Card className={styles.progressCard} bordered={false}>
      <div className={styles.progressContent}>
        <h3 className={styles.progressTitle}>Comparing PDFs</h3>
        <p className={styles.progressSubtitle}>{description}</p>
        <Progress
          percent={Math.round(progress)}
          status="active"
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
          trailColor="#f0f0f0"
          strokeWidth={8}
          showInfo={true}
          format={(percent) => `${percent}%`}
        />
      </div>
    </Card>
  </div>
)

const ChangeLogSkeleton = () => (
  <Card size="small" className={`${styles.pageChangeLog} ${styles.changelogSkeleton}`}>
    <Skeleton active title={{ width: '80%' }} paragraph={{ rows: 3 }} />
    <div className={styles.skeletonSection}>
      <Card size="small" className={styles.skeletonCard}>
        <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 2 }} />
      </Card>
      <Card size="small">
        <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 2 }} />
      </Card>
    </div>
  </Card>
)

const PDFDocumentSkeleton = () => (
  <div className={styles.pdfDocumentSkeleton}>
    {[...Array(3)].map((_, index) => (
      <div key={index} className={styles.skeletonItem}>
        <Skeleton active paragraph={{ rows: 15 }} />
      </div>
    ))}
  </div>
)

const SkeletonOverlay = React.memo(() => (
  <div className={styles.skeletonOverlay}>
    {/* Skeleton for change log section */}
    <div className={styles.sidebarSection}>
      <Skeleton.Input active size="large" style={{ width: '100%', height: '41px' }} />
      <div className={styles.changesLog}>
        {[...Array(3)].map((_, index) => (
          <div key={`skeleton-log-${index}`} className={styles.skeletonLogItem}>
            <ChangeLogSkeleton />
          </div>
        ))}
      </div>
    </div>
    {/* Skeleton for PDF viewers */}
    <div className={styles.contentSection}>
      <div className={styles.pdfContainer}>
        <div className={styles.pdfViewerLeft}>
          <PDFDocumentSkeleton />
        </div>
        <div className={styles.pdfViewerRight}>
          <PDFDocumentSkeleton />
        </div>
      </div>
    </div>
  </div>
))

// Render highlights for text differences
const Highlights = ({ diffs, pageIndex, pdfType, scale = 1.0 }) => {
  return diffs.map((diff, index) => {
    const { oldElement, newElement, type, id } = diff
    const element = pdfType === 'left' ? oldElement : newElement
    if (!element) { return null }

    const [x, y] = element.transform || [0, 0]
    const width = element.width || 0
    const height = element.height || 0
    const style = {
      position: 'absolute',
      left: `${x * scale}px`,
      bottom: `${y * scale}px`,
      width: `${width * scale}px`,
      height: `${height * scale}px`,
      backgroundColor: pdfType === 'left' ? (type === 'removed' ? 'rgba(212, 32, 32, 0.5)' : 'transparent') : (type === 'added' ? 'rgba(149, 212, 32, 0.5)' : 'transparent'),
      zIndex: 1,
    }
    return <div id={id} key={id} style={style} />
  })
}

// Render highlights for graphics differences
const GraphicsHighlights = ({ diffs, pageIndex, pdfType, scale = 1.0, detectionScale = DETECTION_SCALE }) => {
  const diffData = diffs.filter(
    (diff) => {
      const diffPage = diff.page || diff.pageIndex + 1

      if (diff.source === 'pixel-detection' && diffPage === pageIndex + 1) {
        return true
      }
      return (
        diffPage === pageIndex + 1 &&
        diff.type === (pdfType === 'left' ? 'added' : 'removed') // Swapped 'added' and 'removed'
      )
    }
  )
  if (!diffData.length) { return null }

  // Scale conversion factor: coordinates are in detectionScale, need to convert to display scale
  const scaleConversion = scale / detectionScale

  return diffData.map((box, idx) => {
    const { id } = box
    const style = {
      position: 'absolute',
      // Convert from detection scale (3.0) to display scale
      left: `${box.x * scaleConversion}px`,
      top: `${box.y * scaleConversion}px`,
      width: `${box.width * scaleConversion}px`,
      height: `${box.height * scaleConversion}px`,
      backgroundColor: box.source === 'pixel-detection' ? pdfType === 'left' ? 'rgba(212, 32, 32, 0.5)' : 'rgba(149, 212, 32, 0.5)' : (box.type === 'removed' ? 'rgba(149, 212, 32, 0.5)' : 'rgba(212, 32, 32, 0.5)'),
      zIndex: 2,
    }
    return <div id={id} key={id} style={style}></div>
  })
}

// Render placeholders for added or removed pages
const Placeholder = React.memo(({ type, pageHeight }) => (
  <div
    className={classNames(styles.pdfPlaceholder, type === 'added' ? styles.added : styles.removed)}
    style={{ height: pageHeight ? `${pageHeight}px` : '1000px' }}
  >
    <p>{type === 'added' ? 'Page Added →' : '← Page Removed'}</p>
  </div>
))

// Async placeholder component that calculates page height
const AsyncPlaceholder = React.memo(({ type, pageNumber, pdfDoc, scale }) => {
  const [pageHeight, setPageHeight] = useState(1000) // default height

  useEffect(() => {
    const calculateHeight = async () => {
      if (!pdfDoc || !pageNumber) { return }

      try {
        const page = await pdfDoc.getPage(pageNumber)
        const viewport = page.getViewport({ scale: scale })
        setPageHeight(viewport.height)
      } catch (error) {
        console.error('Error calculating page height:', error)
        // Keep default height
      }
    }

    calculateHeight()
  }, [pdfDoc, pageNumber, scale])

  return <Placeholder type={type} pageHeight={pageHeight} />
})

Placeholder.propTypes = {
  type: PropTypes.string.isRequired,
  pageHeight: PropTypes.number,
}

AsyncPlaceholder.propTypes = {
  type: PropTypes.string.isRequired,
  pageNumber: PropTypes.number,
  pdfDoc: PropTypes.object,
  scale: PropTypes.number,
}

// Helper function to extract old and new values from grouped changes
const extractValuesFromGroupedChanges = (groupedChanges) => {
  let oldValue = ''
  let newValue = ''

  groupedChanges.forEach((diff) => {
    if (diff.oldElement) {
      oldValue += `${diff.oldElement.str} `
    }
    if (diff.newElement) {
      newValue += `${diff.newElement.str} `
    }
  })

  return {
    oldValue: oldValue.trim(),
    newValue: newValue.trim(),
  }
}

// Render change log
const ChangeLog = React.memo(({ diffs, pageIndex, checkboxDiffs, scrollToHighlight }) => {
  const pageDiffs = diffs[pageIndex] || []
  const checkboxChanges = checkboxDiffs.filter(diff => {
    const diffPage = diff.page || diff.pageIndex + 1
    return diffPage === pageIndex + 1
  }) // Extract checkbox changes for this page

  // Group diffs by position (x, y)
  const groupedDiffs = pageDiffs.reduce((acc, diff) => {
    const { oldElement, newElement } = diff
    const element = oldElement || newElement
    const [x, y] = element.transform || [0, 0]

    const positionKey = `${x}-${y}`
    if (!acc[positionKey]) {
      acc[positionKey] = []
    }
    acc[positionKey].push(diff)
    return acc
  }, {})

  // Count valid diffs (those with actual content)
  const validDiffsCount = Object.entries(groupedDiffs).filter(([position, groupedChanges]) => {
    const { oldValue, newValue } = extractValuesFromGroupedChanges(groupedChanges)
    return oldValue || newValue // Only count this group if either oldValue or newValue exists
  }).length

  if (validDiffsCount === 0 && checkboxChanges.length === 0) { return null }

  return (
    <Card
      title={
        <div className={styles.pageChangeTitle}>
          PAGE {pageIndex + 1} - CHANGES
        </div>
      }
      size="small"
      className={styles.pageChangeLog}
    >
      {/* Data Points Section */}
      <div className={styles.dataPoints}>
        {/* Grouped Text Changes */}
        {Object.keys(groupedDiffs).length > 0 &&
          Object.entries(groupedDiffs).map(([position, groupedChanges], index) => {
            // Extract old and new values using helper function
            const { oldValue, newValue } = extractValuesFromGroupedChanges(groupedChanges)

            // Only render the section if either oldValue or newValue exists
            if (!oldValue && !newValue) {
              return null // Skip rendering if both old and new are empty
            }

            // Use the first diff in the group for the ID and pdfType
            const firstDiff = groupedChanges[0]
            const lastDiff = groupedChanges[groupedChanges.length - 1]
            const firstDiffId = firstDiff.id
            const lastDiffId = lastDiff?.id
            const pdfType = firstDiff.type === 'added' ? 'right' : 'left'

            const onFindClick = () => {
              scrollToHighlight(firstDiffId, pdfType, pageIndex, lastDiffId)
              const dataPointRef = document.getElementById(`change-log-${pageIndex}-${index}`)
              dataPointRef.scrollIntoView({ behavior: 'smooth' })
            }

            return (
              <Card
                id={`change-log-${pageIndex}-${index}`}
                key={`change-log-${pageIndex}-${index}`}
                size="small"
                className={styles.changeLogCard}
                title={
                  <div className={styles.changeLogHeader}>
                    <span className={styles.changeLogTitle}>Data Point {index + 1}</span>
                    <Tooltip title="Find">
                      <Button
                        size="small"
                        shape='circle'
                        onClick={onFindClick}
                        icon={<FileSearchOutlined />}
                      />
                    </Tooltip>
                  </div>
                }
              >
                {/* Display Old and New only if they exist */}
                {oldValue && (
                  <div className={styles.changeLogContent}>
                    <div className={styles.textChangeInfo}>
                      <div className={`${styles.label} ${styles.oldTextLabel}`}>Old:</div>
                      <div className={`${styles.value} ${styles.oldTextValue}`}>&ldquo;{oldValue}&rdquo;</div>
                    </div>
                  </div>
                )}
                {newValue && (
                  <div className={styles.changeLogContent}>
                    <div className={styles.textChangeInfo}>
                      <div className={`${styles.label} ${styles.newTextLabel}`}>New:</div>
                      <div className={`${styles.value} ${styles.newTextValue}`}>&ldquo;{newValue}&rdquo;</div>
                    </div>
                  </div>
                )}
              </Card>
            )
          })
        }

        {/* Checkbox Changes */}
        {checkboxChanges.length > 0 &&
          checkboxChanges.map((checkboxChange, idx) => {
            const diffId = checkboxChange.id
            const pdfType = checkboxChange.type === 'added' ? 'left' : 'right' // Swapped to match earlier logic

            const onFindClick = () => {
              scrollToHighlight(diffId, pdfType, pageIndex)
              const checkboxRef = document.getElementById(`checkbox-change-${pageIndex}-${idx}`)
              checkboxRef.scrollIntoView({ behavior: 'smooth' })
            }

            return (
              <Card
                id={`checkbox-change-${pageIndex}-${idx}`}
                key={`checkbox-change-${pageIndex}-${idx}`}
                size="small"
                className={styles.changeLogCard}
                title={
                  <div className={styles.changeLogHeader}>
                    <span className={styles.changeLogTitle}>
                      Checkbox {idx + 1}
                    </span>
                    <Tooltip title="Find">
                      <Button
                        size="small"
                        shape='circle'
                        onClick={onFindClick}
                        icon={<FileSearchOutlined />}
                      />
                    </Tooltip>
                  </div>
                }
              >
                <div className={classNames(styles.changeLogCheckboxContent, styles.changeLogContent) }>
                  {/* <span className={styles.checkboxChangeText}>
                    {checkboxChange.type === 'added' ? 'Checkbox checked' : 'Checkbox unchecked'}
                  </span> */}
                  <span className={styles.checkboxChangeText}>
                    Checkbox change detected
                  </span>

                  {/* Show text content if available */}
                  {(checkboxChange.newText || checkboxChange.oldText) && (
                    <div className={styles.textContent}>
                      {checkboxChange.oldText && checkboxChange.oldText !== 'No text found' && checkboxChange.oldText !== '' && (
                        <div className={styles.textChangeInfo}>
                          <div className={`${styles.label} ${styles.oldTextLabel}`}>Old:</div>
                          <div className={`${styles.value} ${styles.oldTextValue}`}>&ldquo;{checkboxChange.oldText}&rdquo;</div>
                        </div>
                      )}
                      {checkboxChange.newText && checkboxChange.newText !== 'No text found' && checkboxChange.newText !== '' && (
                        <div className={styles.textChangeInfo}>
                          <div className={`${styles.label} ${styles.newTextLabel}`}>New:</div>
                          <div className={`${styles.value} ${styles.newTextValue}`}>&ldquo;{checkboxChange.newText}&rdquo;</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            )
          })}
      </div>
    </Card>
  )
})

ChangeLog.propTypes = {
  diffs: PropTypes.array.isRequired,
  pageIndex: PropTypes.number.isRequired,
  checkboxDiffs: PropTypes.array.isRequired,
  scrollToHighlight: PropTypes.func.isRequired,
}

// Simple TooltipSquareRadioButton component for zoom controls
const TooltipSquareRadioButton = ({ icon, onClick, title, disabled }) => (
  <span>
    <Tooltip placement="bottom" title={title}>
      <Radio.Group disabled={disabled}>
        <Radio.Button
          value="selected"
          aria-label={title}
          onClick={onClick}
          style={{ padding: '0 8px' }}
        >
          {icon}
        </Radio.Button>
      </Radio.Group>
    </Tooltip>
  </span>
)

TooltipSquareRadioButton.propTypes = {
  icon: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  title: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
}

export const PDFCompareViewer = React.memo(({ file1, file2 }) => {
  const [numPages1, setNumPages1] = useState(null)
  const [numPages2, setNumPages2] = useState(null)
  const [pdfDoc1, setPdfDoc1] = useState(null)
  const [pdfDoc2, setPdfDoc2] = useState(null)
  const [pdfElements1, setPdfElements1] = useState([])
  const [pdfElements2, setPdfElements2] = useState([])
  const [alignedPages, setAlignedPages] = useState([])
  const [textDiffs, setTextDiffs] = useState([])
  const [pixelCheckboxDiffs, setPixelCheckboxDiffs] = useState([])
  const [scale, setScale] = useState(1.0)
  const [initialScaleCalculated, setInitialScaleCalculated] = useState(false)
  const { comparePages: comparePixelDiffs, isProcessing: isPixelProcessing, progress: pixelProgress } = usePixelDiff()

  const pageRefsLeft = useRef([])
  const pageRefsRight = useRef([])
  const containerRef1 = useRef(null)
  const containerRef2 = useRef(null)
  const contentSectionRef = useRef(null)
  const isSyncingRef = useRef(false)

  // Zoom controls
  const MIN_SCALE = 0.25
  const MAX_SCALE = 5.0
  const ZOOM_STEP = 0.1

  const zoomIn = useCallback(() => {
    setScale(prevScale => Math.min(prevScale + ZOOM_STEP, MAX_SCALE))
  }, [])

  const zoomOut = useCallback(() => {
    setScale(prevScale => Math.max(prevScale - ZOOM_STEP, MIN_SCALE))
  }, [])

  // Calculate initial scale based on available width
  const calculateInitialScale = useCallback(async (pdfDoc) => {
    if (!pdfDoc || !contentSectionRef.current || initialScaleCalculated) { return }

    try {
      // Get the first page to calculate dimensions
      const page = await pdfDoc.getPage(1)
      const viewport = page.getViewport({ scale: 1.0 })
      const pageWidth = viewport.width

      const marginBetweenContainers = MARGIN_BETWEEN_PAGES
      const availableWidth = document.getElementsByClassName(styles.pdfViewerLeft)[0].offsetWidth -
        marginBetweenContainers

      // Calculate scale to fit width
      const calculatedScale = Math.min(availableWidth / pageWidth, 2.0) // Cap at 2.0 for readability
      const finalScale = Math.max(calculatedScale, MIN_SCALE) // Ensure minimum scale

      setScale(finalScale)
      setInitialScaleCalculated(true)
    } catch (error) {
      console.error('Error calculating initial scale:', error)
      // Fallback to default scale
      setScale(1.2)
      setInitialScaleCalculated(true)
    }
  }, [initialScaleCalculated, MIN_SCALE])

  const recalculateScale = useCallback(async () => {
    if (!pdfDoc1 && !pdfDoc2) { return }

    const pdfToUse = pdfDoc1 || pdfDoc2
    try {
      // Get the first page to calculate dimensions
      const page = await pdfToUse.getPage(1)
      const viewport = page.getViewport({ scale: 1.0 })
      const pageWidth = viewport.width

      // Use either left or right container that exists
      const leftContainer = document.getElementsByClassName(styles.pdfViewerLeft)[0]
      const rightContainer = document.getElementsByClassName(styles.pdfViewerRight)[0]

      const marginBetweenContainers = MARGIN_BETWEEN_PAGES
      const availableWidth = (leftContainer || rightContainer).offsetWidth - marginBetweenContainers

      // Calculate scale to fit width
      const calculatedScale = Math.min(availableWidth / pageWidth, 2.0) // Cap at 2.0 for readability
      const finalScale = Math.max(calculatedScale, MIN_SCALE) // Ensure minimum scale

      setScale(finalScale)
    } catch (error) {
      console.error('Error recalculating scale:', error)
    }
  }, [pdfDoc1, pdfDoc2, MIN_SCALE])


  // Synchronize scrolling between PDFs
  const handlePDFScroll = (e) => {
    if (isSyncingRef.current) { return }
    isSyncingRef.current = true

    const container1 = containerRef1.current
    const container2 = containerRef2.current
    const { scrollTop, scrollLeft } = e.target

    if (container1 && e.target === container1) {
      container2.scrollTop = scrollTop
      container2.scrollLeft = scrollLeft
    } else if (container2 && e.target === container2) {
      container1.scrollTop = scrollTop
      container1.scrollLeft = scrollLeft
    }

    isSyncingRef.current = false
  }

  useEffect(() => {
    const container1 = containerRef1.current
    const container2 = containerRef2.current

    if (container1) { container1.addEventListener('scroll', handlePDFScroll) }
    if (container2) { container2.addEventListener('scroll', handlePDFScroll) }

    return () => {
      if (container1) { container1.removeEventListener('scroll', handlePDFScroll) }
      if (container2) { container2.removeEventListener('scroll', handlePDFScroll) }
    }
  }, [numPages1, numPages2])

  // Handle window resize to recalculate scale
  useEffect(() => {
    const handleResize = () => {
      if (pdfDoc1 || pdfDoc2) {
        setInitialScaleCalculated(false)
        const pdfToUse = pdfDoc1 || pdfDoc2
        setTimeout(() => calculateInitialScale(pdfToUse), 100) // Small delay to ensure layout is updated
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [pdfDoc1, pdfDoc2, calculateInitialScale])

  const onDocumentLoadSuccess1 = (pdf) => {
    setNumPages1(pdf.numPages)
    setPdfDoc1(pdf) // Set the first PDF document
    onDocumentLoad(pdf, setPdfElements1)
    calculateInitialScale(pdf) // Calculate scale on first PDF load
  }

  const onDocumentLoadSuccess2 = (pdf) => {
    setNumPages2(pdf.numPages)
    setPdfDoc2(pdf) // Set the second PDF document
    onDocumentLoad(pdf, setPdfElements2)
    if (!pdfDoc1) {
      calculateInitialScale(pdf) // Calculate scale if first PDF hasn't loaded yet
    }
  }

  // Transform pixel detection results to match checkbox diff format
  const transformPixelDiffsToCheckboxFormat = (pixelDiffs) => {
    const checkboxDiffs = []

    pixelDiffs.forEach((pageDiff) => {
      const { pageIndex, differences } = pageDiff

      differences.forEach((diff) => {
        // Only include checkbox-related differences
        if (diff.type === 'checkbox_change' || diff.type === 'possible_checkbox_change') {
          // Determine the type based on text content or classification
          let type = 'added' // Default

          // Check if it's a checkbox being unchecked (old text has 'X', new doesn't)
          if (diff.oldText === 'X' && (diff.newText === '' || diff.newText === 'No text found')) {
            type = 'removed'
          } else if (diff.newText === 'X' && (diff.oldText === '' || diff.oldText === 'No text found')) {
            // Check if it's a checkbox being checked (new text has 'X', old doesn't)
            type = 'added'
          } else if (diff.type === 'possible_checkbox_change') {
            // For possible checkbox changes without clear text, infer from visual differences
            // Use a heuristic or default to 'added' for visual-only changes
            type = 'added'
          }

          checkboxDiffs.push({
            id: diff.id,
            x: diff.x,
            y: diff.y,
            width: diff.width,
            height: diff.height,
            page: pageIndex + 1, // Convert to 1-based page number
            pageIndex: pageIndex, // Keep 0-based for compatibility
            type: type,
            source: 'pixel-detection', // Mark source for identification
            contentValue: diff.contentValue,
            newText: diff.newText,
            oldText: diff.oldText,
          })
        }
      })
    })

    return checkboxDiffs
  }

  const isIntersecting = (diff1, diff2, tolerance = 5 * DETECTION_SCALE) => {
    // Add tolerance to account for small coordinate differences (scaled to match detection scale)
    const rect1 = {
      left: diff1.x - tolerance,
      right: diff1.x + diff1.width + tolerance,
      top: diff1.y - tolerance,
      bottom: diff1.y + diff1.height + tolerance,
    }

    const rect2 = {
      left: diff2.x - tolerance,
      right: diff2.x + diff2.width + tolerance,
      top: diff2.y - tolerance,
      bottom: diff2.y + diff2.height + tolerance,
    }

    return rect1.left < rect2.right &&
           rect1.right > rect2.left &&
           rect1.top < rect2.bottom &&
           rect1.bottom > rect2.top
  }
  // Convert text coordinates to viewport coordinates to match pixel detection system
  const convertTextCoordsToViewport = async (element, pageIndex) => {
    try {
      // Get the page to calculate viewport height
      const pdf = pdfDoc1 || pdfDoc2
      if (!pdf) { return element }
      const page = await pdf.getPage(pageIndex + 1) // pageIndex is 0-based, getPage expects 1-based
      // Use DETECTION_SCALE to match the scale used in pixel detection
      const viewport = page.getViewport({ scale: DETECTION_SCALE })

      // Text coordinates use transform [translateX, translateY]
      // Pixel coordinates use viewport coordinates where Y is flipped
      const [translateX, translateY] = element.transform || [0, 0]

      // Scale coordinates to match detection scale
      return {
        x: translateX * DETECTION_SCALE,
        y: (viewport.height - translateY * DETECTION_SCALE), // Flip Y coordinate to match pixel detection
        width: (element.width || 0) * DETECTION_SCALE,
        height: (element.height || 0) * DETECTION_SCALE,
      }
    } catch (error) {
      console.error('Error converting text coordinates:', error)
      return {
        x: (element.transform?.[0] || 0) * DETECTION_SCALE,
        y: (element.transform?.[1] || 0) * DETECTION_SCALE,
        width: (element.width || 0) * DETECTION_SCALE,
        height: (element.height || 0) * DETECTION_SCALE,
      }
    }
  }

  const removeDuplicateCheckboxes = async (pixelCheckboxes, diffsPerPage) => {
    const filtered = []

    for (const diff of pixelCheckboxes) {
      const pageIndex = diff.page - 1
      const pageDiffs = diffsPerPage[pageIndex] || []

      // Check if any text diff intersects with this pixel diff
      let hasIntersection = false

      for (const textDiff of pageDiffs) {
        const element = textDiff.oldElement || textDiff.newElement
        if (!element) { continue }

        // Convert text coordinates to viewport coordinates
        const elementDiff = await convertTextCoordsToViewport(element, pageIndex)

        if (isIntersecting(diff, elementDiff)) {
          console.log('✓ Intersection found - removing pixel diff')
          hasIntersection = true
          break
        }
      }

      if (!hasIntersection) {
        filtered.push(diff)
      }
    }

    return filtered
  }

  // Process PDFs after elements are extracted
  useEffect(() => {
    const processDiffs = async () => {
      if (pdfElements1.length > 0 && pdfElements2.length > 0) {
        // Align pages
        const aligned = alignPages(pdfElements1, pdfElements2)

        // Add pageIndex explicitly
        const alignedWithIndex = aligned.map((page, index) => ({
          ...page,
          pageIndex: index,
        }))

        setAlignedPages(alignedWithIndex)

        // Compare text on aligned pages
        const diffsPerPage = alignedWithIndex.map((alignedPage, pageIndex) => {
          if (alignedPage.type === 'matched') {
            return compareTextElements(alignedPage.elements1, alignedPage.elements2, pageIndex)
          }
          return []
        })

        setTextDiffs(diffsPerPage)

        const pixelDetectionResults = await comparePixelDiffs(pdfDoc1, pdfDoc2, alignedWithIndex)
        const pixelCheckboxes = transformPixelDiffsToCheckboxFormat(pixelDetectionResults)

        const filteredPixelCheckboxes = await removeDuplicateCheckboxes(pixelCheckboxes, diffsPerPage)
        setPixelCheckboxDiffs(filteredPixelCheckboxes)
      }
    }

    processDiffs()
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [pdfElements1, pdfElements2])

  // Scroll to highlight function
  const scrollToHighlight = useCallback((id, pdfType, pageIndex, lastId = null) => {
    const container = pdfType === 'left' ? containerRef1.current : containerRef2.current
    const pageRef = pdfType === 'left' ? pageRefsLeft.current[pageIndex] : pageRefsRight.current[pageIndex]
    let isScrolling = false

    const attemptScroll = (attemptsLeft) => {
      if (isScrolling) { return }
      isScrolling = true

      // Remove the class from all elements
      const selectedElements = document.getElementsByClassName('highlight-selected')
      if (selectedElements && selectedElements.length) {
        Array.from(selectedElements).forEach(element => {
          element.classList.remove('highlight-selected')
        })
      }

      // Highlight elements on both sides
      const highlightElements = document.querySelectorAll(`#${id}`)
      highlightElements.forEach(element => {
        element.classList.add('highlight-selected')
      })

      const element = document.getElementById(id)

      if (lastId) {
        const lastElement = document.getElementById(lastId)
        lastElement.classList.add('highlight-selected')
      }

      if (element && container && pageRef) {
        // Get the offsetTop of the element relative to the page container
        const elementOffsetTop = element.offsetTop

        // Get the offsetTop of the page container relative to the container
        const pageOffsetTop = pageRef.offsetTop

        // Total offset is the sum of the page's offset and the element's offset within the page
        const totalOffset = pageOffsetTop + elementOffsetTop

        // Adjust the scroll position to bring the element 10 pixels above its current position
        const targetPosition = totalOffset - 44
        container.scrollTo({
          top: targetPosition,
        })

        // Check if the element is positioned correctly (10 pixels above)
        const containerRect = container.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()
        const elementTop = elementRect.top
        const containerTop = containerRect.top
        const targetTop = containerTop + 10
        const isPositioned = Math.abs(elementTop - targetTop) < 5 // Threshold in pixels

        if (!isPositioned && attemptsLeft > 0) {
          // Try again after a delay
          setTimeout(() => attemptScroll(attemptsLeft - 1), 100)
        } else {
          isScrolling = false
        }
      } else if (attemptsLeft > 0) {
        // If the element is not found, wait and try again
        setTimeout(() => attemptScroll(attemptsLeft - 1), 100)
      } else {
        isScrolling = false
      }
    }

    // Start the attempt with a maximum number of retries
    attemptScroll(1000)
  }, [])

  return (
    <div id={styles.pdfCompareViewerOldContainer}>
      {/* Skeleton overlay when loading (similar to OverlayLoader - always render content underneath) */}
      {pixelProgress <= 0 && <SkeletonOverlay />}

      {(isPixelProcessing && pixelProgress > 0) &&
        <ProgressOverlay progress={pixelProgress} />
      }
      {/* Render overlay while processing */}
      <div className={styles.sidebarSection}>

        {pixelProgress > 0 && <ZoomFloatingActions
          zoomIn={scale < MAX_SCALE ? zoomIn : null}
          zoomOut={scale > MIN_SCALE ? zoomOut : null}
          scale={scale}
          recalculateScale={recalculateScale}
        />}

        <div className={styles.changesLog}>
          {!isPixelProcessing && alignedPages.map((_, index) => (
            <div key={`page-log-${index}`}>
              <ChangeLog
                diffs={textDiffs}
                pageIndex={index}
                checkboxDiffs={pixelCheckboxDiffs}
                scrollToHighlight={scrollToHighlight}
              />
            </div>
          ))}
          {((!textDiffs.some(t => t.length > 0) && pixelCheckboxDiffs.length === 0) || isPixelProcessing) && <div className={styles.noChangesLog} data-test-id="pdf-compare-no-changes-log">No changes found.</div>}
        </div>
      </div>
      <div className={styles.contentSection} ref={contentSectionRef}>
        <div className={styles.pdfContainer}>
          <div className={styles.pdfViewerLeft} ref={containerRef1}>
            <Tag color="red" className={styles.pdfTag}>Old</Tag>
            <Document
              file={file1}
              onLoadSuccess={onDocumentLoadSuccess1}
              onLoadError={(error) => console.error('Error loading PDF 1:', error)}
              loading=""
            >
              {alignedPages.map((aligned, index) => (
                <div ref={(el) => (pageRefsLeft.current[index] = el)}
                  key={`page1_${index + 1}`} style={{ position: 'relative' }}>
                  {aligned.type === 'added' ? (
                    <div className={styles.pdfPage}>
                      <AsyncPlaceholder
                        type="added"
                        pageNumber={aligned.pageNum2}
                        pdfDoc={pdfDoc2}
                        scale={scale}
                      />
                    </div>
                  ) : (
                    <Page
                      pageNumber={aligned.pageNum1}
                      scale={scale}
                      renderTextLayer
                      className={styles.pdfPage}
                    />
                  )}
                  {aligned.type !== 'added' && <Highlights diffs={textDiffs[index] || []} pageIndex={index} pdfType="left" scale={scale} />}
                  {aligned.type !== 'added' && <GraphicsHighlights diffs={pixelCheckboxDiffs} pageIndex={index} pdfType="left" scale={scale} />}
                </div>
              ))}
            </Document>
          </div>
          <div className={styles.pdfViewerRight} ref={containerRef2}>
            <Tag color="green" className={styles.pdfTag}>New</Tag>
            <Document
              file={file2}
              onLoadSuccess={onDocumentLoadSuccess2}
              onLoadError={(error) => console.error('Error loading PDF 2:', error)}
              loading=""
            >
              {alignedPages.map((aligned, index) => (
                <div ref={(el) => (pageRefsRight.current[index] = el)} key={`page2_${index + 1}`} style={{ position: 'relative' }}>
                  {aligned.type === 'removed' ? (
                    <div className={styles.pdfPage}>
                      <AsyncPlaceholder
                        type="removed"
                        pageNumber={aligned.pageNum1}
                        pdfDoc={pdfDoc1}
                        scale={scale}
                      />
                    </div>
                  ) : (
                    <Page
                      pageNumber={aligned.pageNum2}
                      scale={scale}
                      renderTextLayer
                      className={styles.pdfPage}
                    />
                  )}
                  {aligned.type !== 'removed' && <Highlights diffs={textDiffs[index] || []} pageIndex={index} pdfType="right" scale={scale} />}
                  {aligned.type !== 'removed' && <GraphicsHighlights diffs={pixelCheckboxDiffs} pageIndex={index} pdfType="right" scale={scale} />}
                </div>
              ))}
            </Document>
          </div>
        </div>
      </div>
    </div>
  )
})

PDFCompareViewer.propTypes = {
  file1: PropTypes.any.isRequired,
  file2: PropTypes.any.isRequired,
}
